import React, { ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { PanelStateless } from '@atlaskit/panel';
import './custom-panel.scss';

export interface CustomPanelProps {
  children?: ReactNode;
  /** Header to render When the panel is in expanded state. */
  expandedHeader?: ReactNode;
  /** Header to render When the panel is in collapsed state. */
  collapsedHeader?: ReactNode;
  /** Defines whether the panel is expanded or collapsed. By default, panel is in collapsed state. */
  isExpanded?: boolean;
  /** This callback is called when panel is expanded/collapsed */
  onChange?: (isExpanded: boolean) => void;
  heading?: React.ReactNode;
  testId?: string;
  version?: number;
  /** needed this prop to update the expanded state after the initial render. */
  isCustomExpanded?: boolean;
  backgroundColor?: string;
  padding?: string;
}

const CustomPanelWrapper = styled.div<{
  backgroundColor?: string;
  padding?: string;
}>`
  .custom-panel-wrapper-v2 {
    background-color: ${(props) => props.backgroundColor};
    > div > div:first-of-type {
      padding: ${(props) => props.padding};
    }
  }
`;

const CustomPanel: React.FC<CustomPanelProps> = ({
  heading,
  children,
  expandedHeader = 'Collapse',
  collapsedHeader = 'Expand',
  isExpanded = false,
  isCustomExpanded = false,
  onChange,
  testId,
  version,
  backgroundColor,
  padding,
}) => {
  const [isExpandedState, setIsExpandedState] = useState(isExpanded);
  let header = isExpandedState ? expandedHeader : collapsedHeader;
  //version 3 is used for new design iteration changes
  const isNewVersion = version && version >= 2;

  /** this effect is to update the expanded state after the initial render */
  useEffect(() => {
    if (isCustomExpanded && isExpanded !== isCustomExpanded) {
      setIsExpandedState(isCustomExpanded);
      if (onChange) {
        onChange(isCustomExpanded);
      }
    }
  }, [isCustomExpanded]);

  useEffect(() => {
    if (version === 3) {
      setIsExpandedState(isExpanded);
    }
  }, [isExpanded]);

  if (typeof header === 'string') {
    header = (
      <h5 data-testid="custom-panel-header" className="panel-heading">
        {header}
      </h5>
    );
  }

  const handleChange = (isExpanded: boolean) => {
    setIsExpandedState(isExpanded);

    if (onChange) {
      onChange(isExpanded);
    }
  };

  return (
    <CustomPanelWrapper
      data-testid={'custom-panel-wrapper' + (testId ? `-${testId}` : '')}
      className="custom-panel-wrapper-container"
      backgroundColor={backgroundColor}
      padding={padding}
    >
      {heading ? (
        typeof heading === 'string' ? (
          <h4 className="margin-bottom">{heading}</h4>
        ) : (
          <div className="margin-bottom">{heading}</div>
        )
      ) : null}
      <div
        className={classNames({
          'custom-panel-wrapper': !isNewVersion,
          'custom-panel-wrapper-v2': isNewVersion,
          'custom-panel-wrapper-v2-collapsed': isNewVersion && !isExpandedState,
        })}
      >
        <PanelStateless header={header} isExpanded={isExpandedState} onChange={handleChange}>
          {children}
        </PanelStateless>
      </div>
    </CustomPanelWrapper>
  );
};

export default CustomPanel;
